const RELOAD_TIMESTAMP_KEY = 'app:lastChunkReload'

export const RELOAD_COOLDOWN_MS = 10_000

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload',
]

function readMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return ''
}

export function isChunkLoadError(error: unknown): boolean {
  const message = readMessage(error)
  if (!message) return false
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern))
}

function getStorage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function reloadOnceForChunkError(now: number = Date.now()): boolean {
  const storage = getStorage()
  const raw = storage?.getItem(RELOAD_TIMESTAMP_KEY)
  const last = raw == null ? null : Number(raw)
  if (
    last !== null &&
    Number.isFinite(last) &&
    now - last < RELOAD_COOLDOWN_MS
  ) {
    return false
  }
  storage?.setItem(RELOAD_TIMESTAMP_KEY, String(now))
  window.location.reload()
  return true
}

export function installChunkErrorRecovery(): () => void {
  const handler = (event: Event) => {
    event.preventDefault()
    reloadOnceForChunkError()
  }
  window.addEventListener('vite:preloadError', handler)
  return () => window.removeEventListener('vite:preloadError', handler)
}

export async function importWithRetry<T>(
  factory: () => Promise<T>,
  retries = 1,
): Promise<T> {
  try {
    return await factory()
  } catch (error) {
    if (retries > 0) {
      return importWithRetry(factory, retries - 1)
    }
    throw error
  }
}
